// Gerenciador central de sons da interface do MÉTRON.
// Web Audio API (tons sintetizados) — sem arquivos externos, leve e confiável.
// Regras: ligado por padrão; preferência salva em localStorage; nunca bloqueia
// a operação principal se o áudio estiver indisponível/bloqueado pelo navegador.
import { toast } from "sonner";

const KEY = "metron_sound_enabled";
let ctx = null;
let lastPlay = 0;
let lastClick = 0;

export function isSoundEnabled() {
  try { return localStorage.getItem(KEY) !== "false"; } catch { return true; }
}
export function setSoundEnabled(v) {
  try { localStorage.setItem(KEY, v ? "true" : "false"); } catch { /* ignore */ }
}

function ensureCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) { try { ctx = new AC(); } catch { return null; } }
  if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
  return ctx;
}

// Desbloqueia o áudio após um gesto legítimo do usuário (políticas dos navegadores).
if (typeof window !== "undefined") {
  const unlock = () => { ensureCtx(); };
  ["pointerdown", "keydown", "touchstart"].forEach((ev) =>
    window.addEventListener(ev, unlock, { passive: true }));
}

function tone(freqs, { duration = 0.16, gain = 0.05, type = "sine" } = {}) {
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  const master = c.createGain();
  master.gain.value = gain;
  master.connect(c.destination);
  freqs.forEach((f, i) => {
    const start = now + i * (duration * 0.6);
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f, start);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(1, start + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0008, start + duration);
    osc.connect(g); g.connect(master);
    osc.start(start); osc.stop(start + duration + 0.03);
  });
}

// Evita sobreposição/duplicidade de sons para uma mesma operação.
function guard() {
  if (!isSoundEnabled()) return false;
  const t = Date.now();
  if (t - lastPlay < 300) return false;
  lastPlay = t;
  return true;
}

export function playSuccess() {
  if (!guard()) return;
  try { tone([784, 1046.5], { duration: 0.17, gain: 0.16, type: "sine" }); } catch { /* no-op */ }
}
export function playError() {
  if (!guard()) return;
  try { tone([311.1, 233.1], { duration: 0.2, gain: 0.14, type: "triangle" }); } catch { /* no-op */ }
}

// Clique curto e limpo para comandos da interface (ação recebida). Debounce próprio, curto.
export function playClick() {
  if (!isSoundEnabled()) return;
  const t = Date.now();
  if (t - lastClick < 90) return;
  lastClick = t;
  try { tone([1180], { duration: 0.05, gain: 0.13, type: "sine" }); } catch { /* no-op */ }
}

// Som ESPECIAL de "instrução finalizada com sucesso": dois tons ascendentes + nota final
// marcante (~0,85s), claramente mais perceptível que a confirmação comum.
export function playFinalize() {
  if (!isSoundEnabled()) return;
  lastPlay = Date.now();
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  const master = c.createGain();
  master.gain.value = 0.24;
  master.connect(c.destination);
  const seq = [
    { f: 659.25, t: 0.00, d: 0.16 },   // E5
    { f: 987.77, t: 0.17, d: 0.16 },   // B5
    { f: 1318.51, t: 0.36, d: 0.5 },   // E6 — final marcante
  ];
  seq.forEach(({ f, t, d }) => {
    const start = now + t;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f, start);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(1, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0008, start + d);
    osc.connect(g); g.connect(master);
    osc.start(start); osc.stop(start + d + 0.05);
  });
}

// Toca um preview mesmo com o debounce (usado ao ligar a preferência).
export function previewSuccess() {
  if (!isSoundEnabled()) return;
  lastPlay = 0;
  playSuccess();
}

// Instala UMA vez: o som acompanha apenas toasts reais de sucesso/erro (confirmações
// de operações concluídas). Seleção de notas/N/O, navegação e diálogos não geram toast,
// logo não produzem som.
let installed = false;
export function installToastSounds() {
  if (installed || !toast) return;
  installed = true;
  const s = typeof toast.success === "function" ? toast.success.bind(toast) : null;
  const e = typeof toast.error === "function" ? toast.error.bind(toast) : null;
  if (s) toast.success = (...args) => { playSuccess(); return s(...args); };
  if (e) toast.error = (...args) => { playError(); return e(...args); };
}

installToastSounds();

// Clique de interface via UM ÚNICO listener delegado (não é por-elemento, então não duplica).
// Toca em botões/links acionáveis; ignora desabilitados e qualquer área marcada data-sound="off"
// (ex.: notas da ficha de avaliação, campos de preenchimento, botões Salvar/Finalizar).
let clickInstalled = false;
export function installClickSounds() {
  if (clickInstalled || typeof document === "undefined") return;
  clickInstalled = true;
  document.addEventListener("pointerdown", (e) => {
    if (!isSoundEnabled()) return;
    if (e.button !== undefined && e.button !== 0) return;
    const t = e.target;
    if (!t || typeof t.closest !== "function") return;
    const el = t.closest('button, [role="button"], a[href]');
    if (!el) return;
    if (el.disabled || el.getAttribute("aria-disabled") === "true") return;
    if (el.closest('[data-sound="off"]')) return;
    playClick();
  }, { passive: true, capture: true });
}

installClickSounds();
